import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { TrackingProvider } from "./lib/context/TrackingContext";
import ABTestDebugger from "@/components/ABTestDebugger";
import Index from "./pages/Index";
import OptimizedIndex from "./pages/OptimizedIndex";
import LaunchPage from "./pages/LaunchPage";
import OfferPage from "./pages/OfferPage";
import NotFound from "./pages/NotFound";
import AdminLeads from "./pages/AdminLeads";
import PublicBooking from "./pages/PublicBooking";
import WaitlistPage from "./pages/WaitlistPage";
import LockInLanding from "./pages/LockInLanding";
import LockInChoosePlan from "./pages/LockInChoosePlan";
import ExternalRedirect from "./components/ExternalRedirect";
import BookDemo from '@/pages/BookDemo';
import ExhaustedLanding from '@/pages/ExhaustedLanding';
import SdrRoiLanding from '@/pages/SdrRoiLanding';
import SoulLanding from '@/pages/SoulLanding';
import ClickerLanding from '@/pages/ClickerLanding';
import ABTestLanding from '@/pages/ABTestLanding';
import ABTestDashboard from '@/pages/ABTestDashboard';
import NoPricingLanding from '@/pages/NoPricingLanding';
import PricingInfo from '@/pages/PricingInfo';
import NoFreeTrialLanding from '@/pages/NoFreeTrialLanding';
import InvestorDeck from '@/pages/InvestorDeck';
import ModalTest from '@/pages/ModalTest';
import EnterpriseLanding from '@/pages/EnterpriseLanding';

const queryClient = new QueryClient();

// The auth app URL - replace with actual production URL
const AUTH_APP_URL = "https://auth.munger.ai";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <TrackingProvider>
        <HashRouter>
          <Routes>
            {/* Make OptimizedIndex the default homepage */}
            <Route path="/" element={<OptimizedIndex />} />
            <Route path="/optimized" element={<OptimizedIndex />} />
            <Route path="/new" element={<Navigate to="/" replace />} />
            <Route path="/old" element={<Index />} />
            
            {/* Keep all landing page variations as separate pages */}
            <Route path="/abtest" element={<ABTestLanding />} />
            <Route path="/abtest-dashboard" element={<ABTestDashboard />} />
            <Route path="/exhausted" element={<ExhaustedLanding />} />
            <Route path="/sdr-cost" element={<SdrRoiLanding />} />
            <Route path="/soul" element={<SoulLanding />} />
            <Route path="/clicker" element={<ClickerLanding />} />
            
            {/* New no-pricing landing page variation */}
            <Route path="/no-pricing" element={<NoPricingLanding />} />
            <Route path="/pricing-info" element={<PricingInfo />} />
            
            {/* Enterprise Landing - No pricing, demo focused */}
            <Route path="/enterprise" element={<EnterpriseLanding />} />
            
            {/* Investor Deck */}
            <Route path="/deck" element={<InvestorDeck />} />
            
            {/* Modal Test Page */}
            <Route path="/modal-test" element={<ModalTest />} />
            
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/launch" element={<LaunchPage />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/offer" element={<OfferPage />} />
            <Route path="/lock-in" element={<LockInLanding />} />
            <Route path="/lock-in-choose-plan" element={<LockInChoosePlan />} />
            <Route path="/login" element={<ExternalRedirect to={`${AUTH_APP_URL}/#/login`} />} />
            <Route path="/sign-up" element={<ExternalRedirect to={`${AUTH_APP_URL}/#/choose-plan`} />} />
            <Route path="/register" element={<ExternalRedirect to={`${AUTH_APP_URL}/#/register`} />} />
            <Route path="/billing" element={<ExternalRedirect to={`${AUTH_APP_URL}/#/billing`} />} />
            <Route path="/overview" element={<ExternalRedirect to={`${AUTH_APP_URL}/#/overview`} />} />
            <Route path="/admin" element={<Navigate to="/admin/leads" replace />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/munger-admin" element={<Navigate to="/admin/leads" replace />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" />} />
          </Routes>
          
          {/* A/B Test Debugger - only shown in development */}
          <ABTestDebugger position="bottom-right" />
        </HashRouter>
      </TrackingProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
// Force rebuild Wed Aug  6 15:57:45 -03 2025
